import {
	Switch,
	SwitchLabel,
	TextField,
	Button,
	Grid,
	Link,
} from '@barracuda-internal/bds-core-v2';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import React, { ReactElement, useEffect, useState } from 'react';
import FullPageError from 'src/components/FullPageError';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import useAPIEffect from 'src/lib/useAPIEffect';
import { ClassifierTextFileView } from 'src/types/dataTypes';
import ulog from 'ulog';
import TextFileUploader from '../TextFileUploader';
import classifierMap from './classifierMap';
import EditViewDialog, { EditViewDialogProps } from './EditViewDialog';
import { validate } from 'src/components/Validation';
import useSettingsStyles from '../styles/settings';

interface EditTextFileDialogProps extends EditViewDialogProps {}
const useStyles = makeStyles((theme) => ({
	alert: {
		marginBottom: theme.spacing(2),
	},
	row: {
		borderBottom: 'thin solid lightgray',
		padding: theme.spacing(2),
	},
	optionCell: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'flex-start',
		paddingTop: '2px',
	},
	fileDrop: {
		padding: theme.spacing(1),
		margin: `0 ${theme.spacing(1)}`,
		textAlign: 'center',
		border: 'thin solid lightgray',
	},
}));

function EditTextFileDialog({
	baseClassifier,
	onClose,
	onSave,
}: EditTextFileDialogProps): ReactElement {
	const details = classifierMap['TextFileFindingView'];
	const { api } = useAppToolchain();
	const classes = useStyles();

	const [isLoading, setIsLoading] = useState(false);
	const [formState, setFormState] = useState({
		wordCount: '',
		caseSensitive: false,
		displayName: '',
		matchWholeWord: true,
		tagName: '',
		disabled: true,
	});

	const [uiError, setUIError] = useState({
		tagName: { error: false, msg: '' },
		displayName: { error: false, msg: '' },
		upload: { error: false, msg: '' },
	});

	const [doSave, setDoSave] = useState(false);
	const [doGet, setDoGet] = useState(true);
	const [doUpload, setDoUpload] = useState<FormData>();
	const [isSaving, setIsSaving] = useState(false);
	const [accessToken, setAccessToken] = useState('');
	const customClasses = useSettingsStyles();
	const [classifier, setClassifier]: [
		ClassifierTextFileView,
		React.Dispatch<ClassifierTextFileView>,
	] = useState();

	const [loadError, setLoadError]: [Error, React.Dispatch<Error>] =
		useState();
	const [saveError, setSaveError] = useState<string>('');
	const defaultSaveErrorMessage = 'Error saving form! Please wait a few moments and try again.';

	const extractErrorMessage = (detail: string): string => {
		const firstLine = detail.split('\n')[0];
		const exceptionMsgMatch = firstLine.match(/Exception:\s*(.+)/);
		return exceptionMsgMatch ? exceptionMsgMatch[1].trim() : firstLine.trim();
	};

	const handleSaveError = async (err) => {
		ulog.error(err);
		try {
			if (err?.response?.json) {
				const errorData = await err.response.json();
				const detail = errorData?.Detail;
				setSaveError(detail ? extractErrorMessage(detail) : defaultSaveErrorMessage);
			} else {
				setSaveError(err?.message || defaultSaveErrorMessage);
			}
		} catch {
			setSaveError(defaultSaveErrorMessage);
		}
	};

	useAPIEffect(
		(cancelled) => {
			if (!doGet) return;
			setIsLoading(true);
			setClassifier(baseClassifier as ClassifierTextFileView);
			if (baseClassifier.id != '') {
				api.get<{ data: ClassifierTextFileView }>(
					baseClassifier.configUrl,
					{},
					{},
					false,
				)
					.then((res) => {
						if (cancelled) return;

						setClassifier(res.data);
						setFormState({
							...formState,
							wordCount: res?.data.wordCount,
							caseSensitive: res?.data.caseSensitive,
							matchWholeWord: res?.data.matchWholeWord,
							displayName: res?.data.displayName || '',
							tagName: res?.data.tagName || '',
							disabled: res?.data.disabled,
						});
					})
					.catch((err) => {
						setLoadError(err);
					})
					.finally(() => {
						if (cancelled) return;
						setIsLoading(false);
						setDoGet(false);
					});
			}
		},
		[doGet],
	);

	function hasUIValidations(uiError: any): boolean {
		const { displayName, tagName } = formState;
		uiError.displayName = {
			error: Boolean(validate({ value: displayName })),
			msg: validate({ value: displayName }),
		};
		uiError.tagName = {
			error: Boolean(validate({ value: tagName })),
			msg: validate({ value: tagName }),
		};
		if (baseClassifier.id == '' && !doUpload) {
			uiError.upload = { error: true, msg: 'File required' };
		} else {
			uiError.upload = { error: false, msg: '' };
		}
		setUIError(uiError);
		return Object.values(uiError).some((field: any) => field.error);
	}
	useAPIEffect(
		(cancelled) => {
			if (!doSave) return;

			if (hasUIValidations(uiError)) {
				// There are validation errors, do not save
				setDoSave(false);
				return;
			}

			const patchObj = [
				{
					path: '/displayName',
					op: 'replace',
					value: formState?.displayName,
				},
				{
					path: '/matchWholeWord',
					op: 'replace',
					value: formState.matchWholeWord,
				},
				{
					path: '/caseSensitive',
					op: 'replace',
					value: formState.caseSensitive,
				},
				{
					path: '/disabled',
					op: 'replace',
					value: formState.disabled,
				},
				{
					path: '/tagName',
					op: 'replace',
					value: formState.tagName,
				},
			];

			setIsSaving(true);
			const save = (id) => {
				api.updateTextFileClassifier(
					baseClassifier.accountId,
					id,
					patchObj,
				)
					.then((res) => {
						if (cancelled) return;
						cancelled = true;
						if (doUpload) {
							api.uploadTextFileView(
								baseClassifier.accountId,
								id,
								doUpload,
							)
								.then((res) => {
									onSave && onSave();
									onClose && onClose();
									setDoGet(true);
								})
								.catch((err) => {
									handleSaveError(err);
								})
								.finally(() => {
									setDoSave(false);
									setIsSaving(false);
								});
						} else {
							onSave && onSave();
							onClose && onClose();
						}
					})
					.catch((err) => {
						if (cancelled) return;
						handleSaveError(err);
					})
					.finally(() => {
						if (cancelled) return;
						setDoSave(false);
						setIsSaving(false);
					});
			};
			if (baseClassifier.id == '') {
				api.createClassifier(baseClassifier.accountId, {
					type: baseClassifier.type,
					disabled: formState.disabled,
					displayName: formState.displayName,
					tagName: formState.tagName,
					caseSensitive: formState.caseSensitive,
					matchWholeWord: formState.matchWholeWord,
				})
					.then((res) => {
						if (cancelled) return;
						cancelled = true;
						if (doUpload) {
							api.uploadTextFileView(
								baseClassifier.accountId,
								res.id,
								doUpload,
							)
								.then(() => {
									onSave && onSave();
									onClose && onClose();
									setDoGet(true);
								})
								.catch((err) => {
									handleSaveError(err);
								})
								.finally(() => {
									setDoSave(false);
									setIsSaving(false);
								});
						} else {
							onSave && onSave();
							onClose && onClose();
						}
					})
					.catch((err) => {
						if (cancelled) return;
						handleSaveError(err);
					})
					.finally(() => {
						if (cancelled) return;
						setDoSave(false);
						setIsSaving(false);
					});
			} else {
				save(baseClassifier.id);
			}
		},
		[doSave],
	);

	useEffect(() => {
		api.getAccessToken().then((token) => setAccessToken(token));
	}, [api]);

	if (!!loadError)
		return <FullPageError message="An error occured while loading." />;

	if (!classifier) return null;

	const handleFileChange = (data: FormData) => {
		setDoUpload(data);
		uiError.upload = { error: false, msg: '' };
		setUIError(uiError);
	};
	function handleFieldChange(
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		fieldName: string,
	) {
		const value = event.target.value;
		const error = validate({ value: value });
		setFormState((prevState) => ({
			...prevState,
			[fieldName]: value,
		}));
		setUIError((prevState) => ({
			...prevState,
			[fieldName]: { error: Boolean(error), msg: error },
		}));
	}

	return (
		<EditViewDialog
			baseClassifier={baseClassifier}
			Icon={details.icon}
			isLoading={false}
			isSaving={false}
			onChange={(newFormState) => {
				ulog.debug('Base form state changed', newFormState);
				setFormState({
					...formState,
					...newFormState,
				});
			}}
			onClose={onClose}
			onSave={() => setDoSave(true)}
			title={details.name}
		>
			<div>
				{!!saveError ? (
					<Alert className={classes.alert} severity="error">
						{saveError}
					</Alert>
				) : null}
				<TextField
					fullWidth
					label="Classifier Name"
					onChange={(event) => {
						handleFieldChange(event, 'displayName');
					}}
					className={customClasses['mb_16']}
					value={formState.displayName}
					variant="outlined"
					required
					inputProps={{ maxLength: 64 }}
					error={uiError.displayName.error}
					helperText={uiError.displayName.msg}
				/>
				<TextField
					label="Label Name"
					fullWidth
					helperText={
						uiError.tagName.error
							? uiError.tagName.msg
							: 'This label will display in your feed when matching text is found.'
					}
					margin="normal"
					onChange={(event) => handleFieldChange(event, 'tagName')}
					value={formState.tagName}
					variant="outlined"
					required
					inputProps={{ maxLength: 64 }}
					error={uiError.tagName.error}
				/>
				<div className={classes.fileDrop}>
					<TextFileUploader onFileChange={handleFileChange} />
				</div>
				{uiError.upload.error ? (
					<p
						className="MuiFormHelperText-root MuiFormHelperText-contained Mui-error MuiFormHelperText-filled MuiFormHelperText-marginDense"
						style={{
							color: '#6F100F',
							fontSize: '12px',
							fontWeight: 'bold',
						}}
					>
						{uiError.upload.msg}
					</p>
				) : null}
				<div
					className={customClasses['text_align_right']}
					style={{ marginTop: '10px' }}
				>
					{classifier?.payload?.url && accessToken ? (
						<Link
							href={`${
								classifier?.payload?.url
							}?access_token=${accessToken}&t=${Date.now()}`}
							target="_blank"
							rel="noopener noreferrer"
							style={{
								textDecoration: 'none',
								display: 'inline-block',
							}}
						>
							<Button>View File</Button>
						</Link>
					) : null}
				</div>
				<h3 style={{ marginBottom: '8px' }}>Options</h3>
				<Grid container className={classes.row}>
					<Grid item xs={10}>
						<h4 style={{ marginTop: 0 }}>Case Sensitivity</h4>
						<p>
							When this option is enabled, the classifier will
							only look for text that matches the exact letter
							case as the specified keywords or phrases.
						</p>
						<p>For example, "cat" will not match "Cat".</p>
					</Grid>
					<Grid item xs={2} className={classes.optionCell}>
						<SwitchLabel
							sx={{ gap: 1 }}
							label={formState.caseSensitive ? 'On' : 'Off'}
							labelPlacement="start"
							control={
								<Switch
									color="primary"
									checked={formState.caseSensitive}
									onChange={(e) =>
										setFormState({
											...formState,
											caseSensitive: e.target.checked,
										})
									}
								/>
							}
						/>
					</Grid>
				</Grid>
				<Grid container className={classes.row}>
					<Grid item xs={10}>
						<h4 style={{ marginTop: 0 }}>Partial Matching</h4>
						<p>
							When this option is enabled, the classifier will
							look for any text that contains the specified
							keywords or phrases and not just exact matches.
						</p>
						<p>
							For example, "cat" will match both "cat" and
							"caterpillar".
						</p>
					</Grid>
					<Grid item xs={2} className={classes.optionCell}>
						<SwitchLabel
							sx={{ gap: 1 }}
							label={!formState.matchWholeWord ? 'On' : 'Off'}
							labelPlacement="start"
							control={
								<Switch
									color="primary"
									checked={!formState.matchWholeWord}
									onChange={(e) =>
										setFormState({
											...formState,
											matchWholeWord: !e.target.checked,
										})
									}
								/>
							}
						/>
					</Grid>
				</Grid>
			</div>
		</EditViewDialog>
	);
}

export default EditTextFileDialog;
